import React from 'react';
import { Button } from '../ui/button';
import { X, AlertCircle } from 'lucide-react';
import { useDiagramStore } from '../../stores/diagramStore';
import { getNodeMutationManager } from '../../core/commands/mutations/NodeMutationManager';
import { Logger } from '../../utils/logger';
import { useTranslation } from '@/hooks/useTranslation';

const logger = Logger.getLoggerWithPrefix('ClearExampleDataBanner');

export const ClearExampleDataBanner: React.FC = () => {
  const { t } = useTranslation('genogram');
  const { t: tCommon } = useTranslation('common');
  const nodes = useDiagramStore(state => state.nodes);
  const updateNode = useDiagramStore(state => state.updateNode);
  const templatesWithExampleData = useDiagramStore(state => state.templatesWithExampleData);
  const exampleDataCleared = useDiagramStore(state => state.exampleDataCleared);
  const markExampleDataCleared = useDiagramStore(state => state.markExampleDataCleared);

  const [dismissed, setDismissed] = React.useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = React.useState(false);

  // Show banner if:
  // 1. A template with example data has been loaded
  // 2. The example data hasn't been cleared yet
  // 3. The banner hasn't been manually dismissed
  const shouldShow = templatesWithExampleData.size > 0 && !exampleDataCleared && !dismissed;

  // Debug logging
  React.useEffect(() => {
    logger.debug('State snapshot', {
      nodes: nodes.length,
      templatesWithExampleData: Array.from(templatesWithExampleData),
      exampleDataCleared,
      dismissed,
      shouldShow
    });
  }, [nodes.length, templatesWithExampleData, exampleDataCleared, dismissed, shouldShow]);

  if (!shouldShow && !isAnimatingOut) {
    return null;
  }

  const handleClearExampleData = () => {
    const mutationManager = getNodeMutationManager();

    // Clear example data from all nodes
    nodes.forEach(node => {
      const hasExampleDataInAttrs = (node.attrs as any)?.exampleData === true;
      const hasExampleDataInProps = (node as any).props?.attrs?.exampleData === true;

      if (hasExampleDataInAttrs || hasExampleDataInProps) {
        const changes: Record<string, any> = {
          name: '',
          age: undefined,
        };

        // If example data is in top-level attrs, clear it
        if (hasExampleDataInAttrs) {
          const { exampleData, ...remainingAttrs } = (node.attrs as any) || {};
          changes.attrs = Object.keys(remainingAttrs).length > 0 ? remainingAttrs : undefined;
        }

        // If example data is in props.attrs, clear it
        if (hasExampleDataInProps) {
          const currentProps = (node as any).props || {};
          const { exampleData, ...remainingAttrs } = currentProps.attrs || {};
          changes.props = {
            ...currentProps,
            attrs: Object.keys(remainingAttrs).length > 0 ? remainingAttrs : undefined
          };
        }

        // Apply changes via mutation manager
        mutationManager.applyNodeChange(node.id, changes);

        // Also update Zustand store
        try {
          updateNode(node.id, changes);
        } catch {}
      }
    });

    // Mark example data as cleared in the diagram store
    markExampleDataCleared();

    handleDismiss();
  };

  const handleDismiss = () => {
    setIsAnimatingOut(true);
    setTimeout(() => {
      setDismissed(true);
      setIsAnimatingOut(false);
    }, 500); // Match animation duration
  };

  return (
    <div
      className={`absolute top-16 left-1/2 -translate-x-1/2 z-50 w-auto transition-all duration-500 ${
        isAnimatingOut
          ? 'opacity-0 scale-50 -translate-x-[45vw] translate-y-[20vh]'
          : 'opacity-100 scale-100'
      }`}
    >
      <div className="bg-blue-50 border border-blue-200 rounded-lg shadow-lg p-3 flex items-center gap-3">
        <AlertCircle className="size-5 text-blue-600 shrink-0" />
        <p className="text-sm text-blue-900 font-medium">
          {t('exampleData.banner.message')}
        </p>
        <Button
          onClick={handleClearExampleData}
          variant="outline"
          size="sm"
          className="bg-white hover:bg-blue-50 text-blue-700 border-blue-300 h-8"
        >
          {t('assistant.clearExampleData')}
        </Button>
        <button
          onClick={handleDismiss}
          className="text-blue-600 hover:text-blue-800 p-1"
          aria-label={tCommon('actions.close')}
        >
          <X className="size-4" />
        </button>
      </div>
    </div>
  );
};
